import { defaultJwtExpiry, getClippingUrlByChannelId, signJwtV2 } from "@core-services/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { DateTimePicker, FormControl, FormControlLabel, TextField } from "@vindral/components"
import { format, isValid, parse } from "date-fns"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { FormSection } from "../atoms/FormSection"
import { HlsVideoPlayer } from "../templates/channel/edit/playout/HlsPlayer"

const DATE_FORMAT = "yyyy-MM-dd HH:mm:ss"
const formatDate = (date: Date | undefined) => (date && isValid(date) ? format(date, DATE_FORMAT) : "")

function parseDate(dateString: string): Date | undefined {
  const date = parse(dateString, DATE_FORMAT, new Date())
  return isNaN(date.getTime()) ? undefined : date
}

const RecordingsPlayerSchema = z.object({
  poster: z.string().optional(),
  from: z
    .string()
    .optional()
    .refine((x) => (x ? !!parseDate(x) : true), `Expected format: ${DATE_FORMAT}`),
  to: z
    .string()
    .optional()
    .refine((x) => (x ? !!parseDate(x) : true), `Expected format: ${DATE_FORMAT}`),
  controls: z.boolean().optional(),
  muted: z.boolean().optional(),
})
type PlayerBuilderSchema = z.infer<typeof RecordingsPlayerSchema>
const resolver = zodResolver(RecordingsPlayerSchema)

interface RecordingsPlayerProps {
  edgeUrl?: string
  channelId: string
  authSecret: string
  from?: string
  to?: string
  onDateRangeChange: (dateRange: { from?: string; to?: string } | undefined) => void
  disabledDates: { before: Date; after: Date } | undefined
}

export function RecordingsPlayer({
  edgeUrl,
  channelId,
  from,
  to,
  disabledDates,
  authSecret,
  onDateRangeChange,
}: RecordingsPlayerProps) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PlayerBuilderSchema>({
    resolver,
    mode: "onChange",
    defaultValues: {
      from: from ? formatDate(new Date(from)) : undefined,
      to: to ? formatDate(new Date(to)) : undefined,
    },
  })
  const values = watch()

  const hlsUrl = useMemo(() => {
    if (!edgeUrl || !channelId) return ""
    const fromDate = values.from ? parseDate(values.from) : undefined
    const toDate = values.to ? parseDate(values.to) : undefined
    if (!fromDate || !toDate) return ""

    const authToken = signJwtV2({
      authSecret,
      channelId,
      scope: {
        clipping: {
          from: fromDate,
          to: toDate,
        },
      },
      expiresAt: defaultJwtExpiry(),
    })

    return getClippingUrlByChannelId({
      edgeUrl,
      channelId,
      from: fromDate,
      to: toDate,
      authToken,
      type: "m3u8",
    })
  }, [edgeUrl, channelId, values.from, values.to, authSecret])

  useEffect(() => {
    const dateRange = !values.from && !values.to ? undefined : { from: values.from, to: values.to }
    onDateRangeChange(dateRange)
  }, [values.from, values.to, onDateRangeChange])

  return (
    <div className="flex flex-col gap-4">
      <FormSection
        title="View"
        description={
          <div className="flex flex-col gap-4">
            <p>Choose a date range to view recordings for this channel.</p>
          </div>
        }
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <FormControl>
            <FormControlLabel error={errors.from?.message}>From</FormControlLabel>
            <div className="flex gap-2">
              <div className="w-48">
                <TextField {...register("from")} error={!!errors?.from} />
              </div>
              <DateTimePicker
                selected={values.from ? parseDate(values.from) : undefined}
                disabledDates={disabledDates}
                onSelect={(date) => {
                  if (isValid(date)) {
                    setValue("from", formatDate(date), { shouldDirty: true, shouldValidate: true })
                  }
                }}
              />
            </div>
          </FormControl>
          <FormControl>
            <FormControlLabel error={errors.to?.message}>To</FormControlLabel>
            <div className="flex gap-2">
              <div className="w-48">
                <TextField {...register("to")} error={!!errors?.to} />
              </div>
              <DateTimePicker
                selected={values.to ? parseDate(values.to) : undefined}
                disabledDates={disabledDates}
                onSelect={(date) => {
                  if (isValid(date)) {
                    setValue("to", formatDate(date), { shouldDirty: true, shouldValidate: true })
                  }
                }}
              />
            </div>
          </FormControl>
        </div>
      </FormSection>
      <HlsVideoPlayer src={hlsUrl} />
    </div>
  )
}
